import type lightGallery from 'lightgallery';
import type { LightGallerySettings } from 'lightgallery/lg-settings';
import type { GalleryItem } from 'lightgallery/lg-utils';

type LightGalleryFactory = typeof lightGallery;
type LightGalleryPlugin = NonNullable<LightGallerySettings['plugins']>[number];

interface GalleryAssets {
    factory: LightGalleryFactory;
    thumbnail: LightGalleryPlugin;
}

class Gallery {
    private readonly selector = '.prose img';
    private readonly host = document.createElement('div');
    private lightGalleryPromise: Promise<GalleryAssets> | null = null;

    constructor() {
        this.host.className = 'hidden';
        this.init();
    }

    private init(): void {
        if (!this.isNeeded()) {
            return;
        }

        document.body.append(this.host);
        this.bindEvents();
    }

    private isNeeded(): boolean {
        return Boolean(document.querySelector('body.single-post') || document.querySelector('body.single-program')) && document.querySelector(this.selector) !== null;
    }

    private bindEvents(): void {
        document.querySelectorAll<HTMLImageElement>(this.selector).forEach((image) => {
            image.addEventListener('click', this.handleImageClick);
        });
    }

    private handleImageClick = async (event: MouseEvent): Promise<void> => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        const image = event.currentTarget;

        if (!(image instanceof HTMLImageElement)) {
            return;
        }

        const proseContainer = image.closest<HTMLElement>('.prose');

        if (!proseContainer) {
            return;
        }

        const images = Array.from(proseContainer.querySelectorAll<HTMLImageElement>('img'));
        const index = images.indexOf(image);

        if (index === -1 || images.length === 0) {
            return;
        }

        event.preventDefault();

        const galleryItems = images.map((galleryImage) => this.buildGalleryItem(galleryImage));
        const { factory, thumbnail } = await this.loadLightGallery();
        const gallery = factory(this.host, this.getOptions(galleryItems, thumbnail));

        this.host.addEventListener('lgAfterClose', () => {
            gallery.destroy();
        }, { once: true });

        gallery.openGallery(index);
    };

    private async loadLightGallery(): Promise<GalleryAssets> {
        if (!this.lightGalleryPromise) {
            this.lightGalleryPromise = Promise.all([
                import('lightgallery'),
                import('lightgallery/plugins/thumbnail'),
                import('lightgallery/css/lightgallery.css'),
                import('lightgallery/css/lg-thumbnail.css'),
            ]).then(([module, thumbnailModule]) => ({
                factory: module.default,
                thumbnail: thumbnailModule.default,
            }));
        }

        return this.lightGalleryPromise;
    }

    private buildGalleryItem(image: HTMLImageElement): GalleryItem {
        const link = image.closest<HTMLAnchorElement>('a[href]');
        const figure = image.closest<HTMLElement>('figure');
        const figcaption = figure?.querySelector<HTMLElement>('figcaption');
        const source = link?.href || image.currentSrc || image.src;
        const caption = figcaption?.innerHTML.trim() || image.alt.trim();

        return {
            src: source,
            thumb: image.currentSrc || image.src,
            subHtml: caption ? `<div class="lg-sub-html">${caption}</div>` : '',
            alt: image.alt,
        };
    }

    private getOptions(dynamicEl: GalleryItem[], thumbnail: LightGalleryPlugin): LightGallerySettings {
        return {
            dynamic: true,
            dynamicEl,
            plugins: [thumbnail],
            thumbnail: true,
            animateThumb: true,
            download: false,
            counter: dynamicEl.length > 1,
            selector: 'this',
            licenseKey: '0000-0000-000-1111',
        };
    }
}

export default Gallery;